import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Sparkles, Clock } from "lucide-react";
import React, { useState } from "react";
import { useBillingSubscription } from "@/hooks/use-billing-subscription";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

function HeaderTrialProgressComponent() {
  const { displayRules, trialInfo } = useBillingSubscription();
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Use centralized logic for display rules and trial info
  const shouldShowUpgrade = displayRules.shouldShowUpgradePrompts && trialInfo.isActive;
  const { daysRemaining, progressPercentage } = trialInfo;

  const handleUpgrade = async () => {
    if (isUpgrading) return;
    
    setIsUpgrading(true);
    try {
      // Import and use the centralized checkout hook functionality
      const { getRefgrowReferralCode } = await import('@/lib/refgrow');
      const referralCode = getRefgrowReferralCode();
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          priceId: 'pro-monthly',
          referralCode
        }
      });
      
      if (error) {
        console.error('Error creating checkout:', error);
        toast.error('Failed to create checkout session');
        return;
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create checkout session');
    } finally {
      setIsUpgrading(false);
    }
  };

  // Don't show if we shouldn't show upgrade
  if (!shouldShowUpgrade) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 bg-white rounded-md px-2.5 py-1.5 border border-black/10 shadow-sm relative z-10 max-w-fit hover:shadow-md transition-shadow">
      <div className="flex items-center gap-1">
        <Clock className="h-3.5 w-3.5 text-black-500" />
        <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
          {daysRemaining}d
        </span>
      </div>
      
      <div className="w-12">
        <Progress 
          value={progressPercentage} 
          className="h-1.5 w-full bg-red-500/10 max-w-[130px] [&>div]:bg-red-500"
        />
      </div>
      
      <Button 
        onClick={() => handleUpgrade()}
        disabled={isUpgrading}
        size="sm"
        className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 hover:from-purple-700 hover:via-pink-700 hover:to-orange-600 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 border-0 text-xs px-2.5 py-1 h-6"
      >
        <Sparkles className="h-3 w-3 mr-0" />
        {isUpgrading ? '...' : 'Upgrade'}
      </Button>
    </div>
  );
}

export const HeaderTrialProgress = React.memo(HeaderTrialProgressComponent); 